import { detectHost } from "./detect-host";
import { newRequestId } from "./request-id";
import { DASHBOARD_ORIGIN } from "../config/studio-env";
import type { AuthErrorMessage, AuthHostMessage, AuthRequest, EditorToken } from "./types";

export const REQUEST_TIMEOUT_MS = 8_000;

export class HostAuthError extends Error {
  constructor(
    public code: AuthErrorMessage["code"],
    message?: string,
  ) {
    super(message ?? `Host auth error: ${code}`);
    this.name = "HostAuthError";
  }
}

/**
 * Sends one TEMPLATE_EDITOR_AUTH_REQUEST to whichever host we are embedded in
 * and resolves with the token, or rejects on AUTH_ERROR / timeout.
 *
 * Framework-agnostic on purpose — no React, so TokenManager can own it and it
 * stays unit-testable.
 */
export function requestTokenFromHost(): Promise<EditorToken> {
  const host = detectHost();
  if (host === "none") {
    return Promise.reject(
      new Error(
        "Editor is not embedded in a recognized host (no iframe parent, no WebView bridge).",
      ),
    );
  }

  const requestId = newRequestId();
  const request: AuthRequest = { type: "TEMPLATE_EDITOR_AUTH_REQUEST", requestId };

  return new Promise<EditorToken>((resolve, reject) => {
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    const timeoutId = setTimeout(() => {
      finish(() =>
        reject(new Error("Timed out waiting for host to respond with an editor token.")),
      );
    }, REQUEST_TIMEOUT_MS);

    function handleIncoming(data: unknown) {
      if (!data || typeof data !== "object") return;
      const msg = data as AuthHostMessage;
      if (msg.requestId !== requestId) return; // a reply for someone else

      if (msg.type === "TEMPLATE_EDITOR_AUTH_TOKEN") {
        // Trust the origin, verify the shape: a version-skewed host that
        // omits or mangles expiresInSec must produce a typed rejection, not
        // an EditorToken of undefineds that later becomes setTimeout(cb, NaN).
        if (
          typeof msg.editorToken !== "string" ||
          msg.editorToken.length === 0 ||
          typeof msg.expiresAt !== "string" ||
          !Number.isFinite(msg.expiresInSec) ||
          msg.expiresInSec <= 0
        ) {
          finish(() =>
            reject(
              new HostAuthError(
                "UNAVAILABLE",
                "Host replied with a malformed token payload.",
              ),
            ),
          );
          return;
        }
        finish(() =>
          resolve({
            value: msg.editorToken,
            expiresAt: msg.expiresAt,
            expiresInSec: msg.expiresInSec,
          }),
        );
      } else if (msg.type === "TEMPLATE_EDITOR_AUTH_ERROR") {
        finish(() => reject(new HostAuthError(msg.code, msg.message)));
      }
    }

    // --- iframe channel ---------------------------------------------------
    const onWindowMessage = (event: MessageEvent) => {
      // MANDATORY, and it must be the first thing that runs: any script on the
      // page can postMessage into this window. Nothing about event.data is
      // read until the origin matches exactly.
      if (event.origin !== DASHBOARD_ORIGIN) return;
      handleIncoming(event.data);
    };

    // --- WebView channel --------------------------------------------------
    // Native replies by invoking this one global with the same message shape
    // the iframe channel uses. There is no origin to check because this is not
    // a window channel at all — the bridge object exists only because native
    // code injected it into this exact page.
    const onNativeCallback = (payload: unknown) => handleIncoming(payload);

    function cleanup() {
      clearTimeout(timeoutId);
      window.removeEventListener("message", onWindowMessage);
      delete window.__onTemplateEditorAuth;
    }

    try {
      // Exactly one channel, matching what we detected.
      if (host === "iframe") {
        window.addEventListener("message", onWindowMessage);
        window.parent.postMessage(request, DASHBOARD_ORIGIN);
      } else if (host === "ios-webview") {
        window.__onTemplateEditorAuth = onNativeCallback;
        window.webkit!.messageHandlers!.templateEditorAuth!.postMessage(request);
      } else {
        window.__onTemplateEditorAuth = onNativeCallback;
        window.templateEditorAuth!.postMessage(JSON.stringify(request));
      }
    } catch (postError) {
      // A synchronous bridge throw otherwise strands the 8s timer and the
      // global callback until the timeout self-cleans. finish() routes
      // through cleanup() on this path too.
      finish(() =>
        reject(postError instanceof Error ? postError : new Error(String(postError))),
      );
    }
  });
}
